import clsx from "clsx";
import { IconChevronDown } from "@tabler/icons-react";
import Link from "next/link";
import {
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  MenuSection,
  MenuSeparator,
} from "@headlessui/react";

import { formatDate } from "@chef/helpers";
import { Skeleton } from "@chef/components";
import { ChevronLeft, ChevronRight } from "@chef/icons/small";

import { useWeeks, type WeekBlock } from "./useWeeks";

import { intl } from "./WeeksNavigation.Intl";

interface AdjacentWeekButtonProps {
  type: "next" | "prev";
  week: WeekBlock;
}

const AdjacentWeekButton = ({ week, type }: AdjacentWeekButtonProps) => {
  const icon = type === "prev" ? <ChevronLeft /> : <ChevronRight />;

  if (week.type === "separator" || week.type === "skeleton") {
    return null;
  }

  if (week.type === "none") {
    return (
      <span className="items-center justify-center hidden h-8 px-2 rounded-lg md:flex text-grey-1/75">
        {icon}
      </span>
    );
  }

  return (
    <Link
      href={week.href}
      className="items-center justify-center hidden h-8 px-2 rounded-lg md:flex hover:bg-grey-2"
    >
      {icon}
    </Link>
  );
};

export const WeeksNavigation = () => {
  const { weeks } = useWeeks();

  if (!weeks.isReady) {
    return (
      <Skeleton animation="pulse" height={12} width={48} variant="rounded" />
    );
  }

  let message: string, status: string;

  if (weeks.current.type === "my-menu") {
    message = formatDate(
      new Date(weeks.current.deliveryDate),
      intl.SHORT_DATE_FORMAT,
    );

    status = `${intl.MAKE_CHANGES_BY} ${formatDate(
      new Date(weeks.current.cutoffDateTime),
      intl.LONG_DATE_FORMAT,
    )}`;
  } else if (weeks.current.type === "my-delivery") {
    message = formatDate(
      new Date(weeks.current.deliveryDate),
      intl.SHORT_DATE_FORMAT,
    );
    status = weeks.current.status;
  }

  return (
    <div className="flex items-center gap-1">
      <AdjacentWeekButton week={weeks.prev} type="prev" />
      <Menu>
        {({ open }) => (
          <>
            <MenuButton
              id="delivery-date-button"
              className="flex flex-col items-center justify-center w-48 h-12 p-2 rounded-2xl bg-grey-3 hover:bg-grey-2 group"
            >
              <div className="flex items-center justify-center gap-1 border-b border-transparent group-hover:border-black">
                <strong className="text-sm">{message}</strong>
                <IconChevronDown
                  size={16}
                  className={clsx(
                    "transition-transform duration-350",
                    open && "rotate-180",
                  )}
                />
              </div>
              <p className="text-sm text-grey-1 text-nowrap">{status}</p>
            </MenuButton>

            <MenuItems
              anchor="bottom"
              className="z-50 shadow frosted-glass rounded-3xl"
            >
              <div
                id="my-menu-select-week-modal"
                className="flex flex-col gap-1 p-2 w-80"
              >
                {weeks.render().map((w, i) => {
                  if (w.type === "none") {
                    return null;
                  }

                  if (w.type === "separator") {
                    return (
                      <MenuSeparator
                        key={`weeks-navigation-separator-${i}`}
                        className="h-0.5 my-1 bg-grey-2"
                      />
                    );
                  }

                  if (w.type === "skeleton") {
                    return (
                      <Skeleton
                        key={`weeks-navigation-skeleton-${i}`}
                        animation="pulse"
                        height={9}
                        width="100%"
                        variant="rounded"
                      />
                    );
                  }

                  return (
                    <MenuSection key={`${w.week}-${w.year}`}>
                      <MenuItem data-testid={`weeks-navigation-week-${w.week}`}>
                        {({ focus }) => {
                          const isActive =
                            weeks.current.week === w.week &&
                            weeks.current.year === w.year;

                          const isHighlighted = isActive || focus;

                          return (
                            <Link
                              href={w.href}
                              key={w.week}
                              className={clsx(
                                "flex justify-between rounded-lg p-2 align-middle",
                                isHighlighted &&
                                  "bg-primary text-white font-bold",
                              )}
                              passHref
                            >
                              <span className="text-sm/5">{w.message}</span>
                              <span
                                className={clsx(
                                  "text-sm/5",
                                  !isHighlighted && "text-grey-1",
                                )}
                              >
                                {w.status}
                              </span>
                            </Link>
                          );
                        }}
                      </MenuItem>
                    </MenuSection>
                  );
                })}
              </div>
            </MenuItems>
          </>
        )}
      </Menu>
      <AdjacentWeekButton week={weeks.next} type="next" />
    </div>
  );
};
